import { memo, useContext } from 'react';
import { makeStyles } from '@mui/styles';
import { Box, Theme, Typography } from '@mui/material';
import { AccountBalanceWalletOutlined, AddCircleOutlineOutlined } from '@mui/icons-material';

import { Icon } from '../../../../../../common/icon';
import Tooltip from '../../../../../../common/tooltip';
import { Dialog } from '../../../../../../common/dialog';
import { HEIGHT_OF_DESKTOP_HEAD } from '../../../consts';
import { WithLoader } from '../../../../../../common/loader';
import { CARD_BOX_SHADOW, RADIUS } from '../../../../../../../styles/themes';
import { useDialog } from '../../../../../../common/dialog/useDialog';
import { formatToAmount } from '../../../../../../../constants/helpers/utils';

import { ClientBalanceContext, ClientBalanceAmount, balanceAmountColorMapper } from '../../../../client-balance';
import AddFundsToYourWallet from '../../../../client-balance/add-funds-to-your-wallet';

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    display: 'flex',
    boxShadow: CARD_BOX_SHADOW,
    background: theme.palette.common.white,
    borderRadius: RADIUS.lg,
    padding: theme.spacing(1, 2),
    height: HEIGHT_OF_DESKTOP_HEAD
  },
  walletIconContainer: {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(2)
  },
  walletIcon: {
    fontSize: '32px'
  },
  balanceLabel: {
    fontSize: '16px',
    color: theme.palette.common.black
  },
  amount: {
    fontSize: '18px',
    lineHeight: '21px'
  },
  addAmountIconContainer: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: theme.spacing(2)
  },
  addAmountIcon: {
    display: 'flex',
    fontSize: '24px',
    alignItems: 'center',
    cursor: 'pointer',
    color: theme.palette.common.black,
    '&:hover': {
      color: theme.palette.primary.main
    }
  }
}));

const UserWalletCard = () => {
  const classes = useStyles();
  const { isDialogOpen, handleOpenDialog, handleCloseDialog } = useDialog();

  const { getClientBalance } = useContext(ClientBalanceContext);

  const balance = getClientBalance('balance') as ClientBalanceAmount;

  return (
    <>
      <Box className={classes.card} data-testid="balance-card">
        <Box className={classes.walletIconContainer}>
          <Icon icon={AccountBalanceWalletOutlined} className={classes.walletIcon} />
        </Box>
        <WithLoader loading={balance === null} size={24}>
          {balance ? (
            <Box>
              <Typography className={classes.balanceLabel}>Balance</Typography>
              <Typography color={balanceAmountColorMapper({ balance, isMobile: false })} className={classes.amount}>
                {formatToAmount(balance)}
              </Typography>
            </Box>
          ) : null}
          <Box className={classes.addAmountIconContainer}>
            <Tooltip title="Add funds to your wallet" placement="bottom">
              <Box onClick={handleOpenDialog} data-testid="add-funds-button">
                <Icon icon={AddCircleOutlineOutlined} className={classes.addAmountIcon} />
              </Box>
            </Tooltip>
          </Box>
        </WithLoader>
      </Box>
      <Dialog
        open={isDialogOpen}
        title="Adding funds to the wallet"
        handleClose={handleCloseDialog}
        ignoreMinHeight={true}
      >
        <AddFundsToYourWallet handleClose={handleCloseDialog} />
      </Dialog>
    </>
  );
};
export default memo(UserWalletCard);
